import type { Interpolation } from "@emotion/react";
import { useRouter, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { AdminDashUserResponse } from "@packages/sdk";
import { useRequestCommunitiesAdminMember } from "@packages/sdk";
import {
  cssMediaQueryMinWidthTablet,
  numericPercentages,
  numericPixels,
  spacing,
  useAlert,
  useConfirm,
} from "@packages/ui";

const onlyAdminModalContentStyles: Interpolation = {
  margin: spacing.m,
  maxWidth: numericPercentages[100],
  [cssMediaQueryMinWidthTablet]: {
    margin: spacing.none,
    maxWidth: numericPixels[360],
  },
};

type UseAdminDashMemberActionsProps = {
  communityId: number;
  user: AdminDashUserResponse & { is_only_admin?: boolean };
};

export const useAdminDashMemberActions = ({
  communityId,
  user,
}: UseAdminDashMemberActionsProps) => {
  const t = useTranslations();
  const analytics = useAnalytics();
  const onlyAdminAlert = useConfirm({
    primaryButton: t("onboarding_button_continue"),
    cancelButton: t("general_word_cancel"),
    modalProps: { contentCSS: [onlyAdminModalContentStyles] },
  });
  const router = useRouter();
  const alert = useAlert();

  const {
    mutationDelete: removeUserFromGroup,
    mutationPostAdminGrant: grantAdminRights,
    mutationPostAdminRevoke: revokeAdminRights,
  } = useRequestCommunitiesAdminMember({
    communityId: communityId,
    userId: user?.id,
    mutationProps: {
      onSuccess: () => {
        if (user.is_self && user.is_admin) {
          // This is to prevent the user from accessing PAD if they no longer have admin rights to any community
          router.refresh();
        }
      },
      onError: (error) => {
        alert.show({
          title: t("general_word_error"),
          description: error.message,
        });
      },
    },
  });

  const handleRemoveFromGroup = () => {
    if (user.is_only_admin) {
      onlyAdminAlert.show({
        title: t("pad_choose_a_new_admin"),
        description: user.is_self
          ? t("pad_before_you_leave_the_group")
          : t("pad_choose_a_different_admin"),
      });
    } else {
      analytics.track({
        event: user.is_self
          ? HallowAnalyticsEvent.TappedLeaveGroup
          : HallowAnalyticsEvent.TappedRemoveCommunityMember,
      });
      removeUserFromGroup.mutate();
    }
  };

  const handleChangeAdminRights = () => {
    if (user.is_admin) {
      analytics.track({
        event: HallowAnalyticsEvent.TappedAssignAsMember,
      });
      if (user.is_only_admin) {
        onlyAdminAlert.show({
          title: t("pad_choose_a_new_admin"),
          description: user.is_self
            ? t("pad_before_you_assign_yourself_as_member")
            : t("pad_choose_a_different_admin"),
        });
      } else {
        revokeAdminRights.mutate();
      }
    } else {
      analytics.track({
        event: HallowAnalyticsEvent.TappedAssignAsAdmin,
      });
      grantAdminRights.mutate();
    }
  };

  return { handleRemoveFromGroup, handleChangeAdminRights };
};
